import type { IntroData } from "@/types/pageData";
import { logError } from "@/utils/logger";

export interface Input {
  cards: IntroData["cards"];
  startDelay?: number;
  interval?: number;
  duration?: number;
  staggerDelay?: number;
}

export interface State {
  index: number;
  productCardAnimatedValue: number;
  imagesLoaded: Record<number, boolean>;
}

const PEEKABOO_ENTER_DELAY = 50;
const PEEKABOO_LEAVE_OFFSET = 400;

export default class CastIntroCards extends Marko.Component<Input, State> {
  // Potentially could have one timeout for both animations?
  cardIndexTimeout?: NodeJS.Timeout;
  peekabooCardsTimeout?: NodeJS.Timeout;
  observer?: IntersectionObserver;
  inView = false;

  onCreate() {
    this.state = {
      index: 0,
      productCardAnimatedValue: 0,
      imagesLoaded: {},
    };
  }

  async onMount() {
    // Set up observer
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !this.inView) {
          clearTimeout(this.peekabooCardsTimeout);
          clearTimeout(this.cardIndexTimeout);

          // Peekaboo effect happens just before the card is in view
          this.peekabooCardsTimeout = setTimeout(() => {
            this.state.productCardAnimatedValue = 0;
          }, 500 - PEEKABOO_LEAVE_OFFSET);

          // Trottle the inView state to prevent multiple calls
          // Technically first render will be startDelay + 500ms
          this.cardIndexTimeout = setTimeout(() => {
            this.inView = true;
            this.updateCardIndex();
          }, 500);
        } else {
          this.inView = false;

          // Clear both timeouts
          clearTimeout(this.cardIndexTimeout);
          clearTimeout(this.peekabooCardsTimeout);
        }
      });
    });

    // Timer for coordinating animation starting
    const startTime = new Date().getTime();

    // Preload first set of images, sequential so we can show first set of images before animating
    await this.preloadImages(0);

    this.state.productCardAnimatedValue = 1;

    // Preload second set of images
    await this.preloadImages(1);

    // Delay start of first animation after 2nd set of images are loaded
    const endTime = new Date().getTime();
    const timeElapsed = endTime - startTime;
    const startDelay = this.input.startDelay || 1500;
    const delay = Math.max(0, startDelay - timeElapsed);

    setTimeout(() => {
      this.observer?.observe(this.getEl("container"));
    }, delay);
  }

  loadImage(src: string) {
    const img = new Image();
    img.src = src;
    return img.decode().catch((error) => {
      logError(new Error(`Failed to decode image: ${src}`), {
        imageSrc: src,
        originalError: error.message,
        component: "cast-intro-cards-two",
      });
      // Return a resolved promise to continue with the animation flow
      // even if image fails to load
      return Promise.resolve();
    });
  }

  preloadImages(index: number) {
    const currentCard = this.input.cards[index];
    const influencerImage = currentCard.influencer.image;
    const [productCard1, productCard2] = currentCard.products;

    const promises = [
      this.loadImage(influencerImage),
      this.loadImage(productCard1),
      this.loadImage(productCard2),
    ];

    // Return promise that also updates state
    // Use allSettled to continue even if some images fail
    return Promise.allSettled(promises)
      .then((results) => {
        // Log any failed image loads
        results.forEach((result, idx) => {
          if (result.status === "rejected") {
            const imageSrc = [influencerImage, productCard1, productCard2][idx];
            logError(new Error(`Failed to preload image at index ${idx}`), {
              imageSrc,
              cardIndex: index,
              component: "cast-intro-cards-two",
            });
          }
        });

        this.state.imagesLoaded[index] = true;
        this.setStateDirty("imagesLoaded");
      })
      .catch((error) => {
        // Fallback error handling
        logError(new Error("Critical error in preloadImages"), {
          cardIndex: index,
          component: "cast-intro-cards-two",
          originalError: error.message,
        });

        // Still mark as loaded to continue animation
        this.state.imagesLoaded[index] = true;
        this.setStateDirty("imagesLoaded");
      });
  }

  updateCardIndex() {
    const interval = this.input.interval || 3000;
    this.state.index = this.state.index + 1;

    setTimeout(() => {
      this.state.productCardAnimatedValue = 1;
    }, PEEKABOO_ENTER_DELAY);

    if (!this.inView) {
      return;
    }

    // Next index to animate to
    const nextIndex = (this.state.index + 1) % this.input.cards.length;

    // Timer for coordinating animation starting
    const startTime = new Date().getTime();

    // If the next set of images are not preloaded, we'll preload them
    if (!this.state.imagesLoaded[nextIndex]) {
      this.preloadImages(nextIndex).then(() => {
        // We will try to trigger the next animation with our set interval
        // Fires immediately if preloading has taken too long
        const endTime = new Date().getTime();
        const timeElapsed = endTime - startTime;
        const timeoutInterval = Math.max(0, interval - timeElapsed);

        this.peekabooCardsTimeout = setTimeout(
          () => {
            this.state.productCardAnimatedValue = 0;
          },
          Math.max(0, timeoutInterval - PEEKABOO_LEAVE_OFFSET),
        );

        this.cardIndexTimeout = setTimeout(() => {
          this.updateCardIndex();
        }, timeoutInterval);
      });
      return;
    }

    // Images are preloaded, trigger the next animation with the interval
    this.peekabooCardsTimeout = setTimeout(
      () => {
        this.state.productCardAnimatedValue = 0;
      },
      Math.max(0, interval - PEEKABOO_LEAVE_OFFSET),
    );

    this.cardIndexTimeout = setTimeout(() => {
      requestAnimationFrame(() => {
        this.updateCardIndex();
      });
    }, interval);
  }

  getInfluencerCards(cards: IntroData["cards"]) {
    return cards.map((card) => card.influencer);
  }
}
